export const DEFAULT_AVATAR = "/img/default_avatar.webp";
export const DEFAULT_USER_AVATAR = "/img/user_avatar.jpg";

export const DEFAULT_GROUP_FORM = {
  nom: "",
  ordre_groupe: 0,
  color_groupe: "bg-blue-400",
  is_active: true,
};

export const DEFAULT_MESSAGE_DRAFT = {
  subject: "",
  content: "",
};

export const ROLE_LIMITS = {
  1: { maxGroups: 20, maxAdherents: Infinity },
  5: { maxGroups: 1, maxAdherents: 10 },
  6: { maxGroups: 5, maxAdherents: 50 },
  7: { maxGroups: 10, maxAdherents: Infinity },
};

export const GROUP_COLOR_OPTIONS = [
  { value: "bg-blue-400", label: "Bleu" },
  { value: "bg-green-400", label: "Vert" },
  { value: "bg-red-400", label: "Rouge" },
  { value: "bg-yellow-400", label: "Jaune" },
  { value: "bg-purple-400", label: "Violet" },
  { value: "bg-pink-400", label: "Rose" },
  { value: "bg-gray-400", label: "Gris" },
];

export const GROUP_COLOR_STYLES = {
  "bg-blue-400": {
    header: "from-blue-600 to-sky-500",
    dot: "bg-blue-500",
    badge:
      "bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-200",
  },

  "bg-green-400": {
    header: "from-emerald-600 to-green-500",
    dot: "bg-emerald-500",
    badge:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
  },

  "bg-red-400": {
    header: "from-red-600 to-rose-500",
    dot: "bg-red-500",
    badge:
      "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
  },

  "bg-yellow-400": {
    header: "from-amber-500 to-yellow-400",
    dot: "bg-amber-500",
    badge:
      "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200",
  },

  "bg-purple-400": {
    header: "from-purple-600 to-indigo-500",
    dot: "bg-purple-500",
    badge:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
  },

  "bg-pink-400": {
    header: "from-pink-600 to-rose-500",
    dot: "bg-pink-500",
    badge:
      "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-200",
  },

  "bg-gray-400": {
    header: "from-slate-700 to-slate-500",
    dot: "bg-slate-500",
    badge:
      "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70",
  },
};

export function getUserId(user) {
  return user?.userId || user?.id || null;
}

export function getUserFullName(user) {
  return [user?.prenom, user?.nom].filter(Boolean).join(" ") || "Coach";
}

export function getRoleLimits(roleId) {
  return (
    ROLE_LIMITS[roleId] || {
      maxGroups: 0,
      maxAdherents: 0,
    }
  );
}

export function formatLimit(value) {
  return value === Infinity ? "∞" : value;
}

export function getProgressPercent(value, max) {
  if (!max || max === Infinity) return 0;

  return Math.min(Math.round((value / max) * 100), 100);
}

export function sortGroups(groups = []) {
  return [...groups].sort((a, b) => {
    const orderA = Number(a?.ordre_groupe || 0);
    const orderB = Number(b?.ordre_groupe || 0);

    if (orderA !== orderB) return orderA - orderB;

    return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
  });
}

export function normalizeGroupPayload(payload) {
  return {
    ...payload,
    nom: String(payload.nom || "").trim(),
    ordre_groupe: Number(payload.ordre_groupe || 0),
    color_groupe: payload.color_groupe || "bg-blue-400",
    is_active: payload.is_active !== false,
  };
}

export function getGroupColorStyle(color) {
  return GROUP_COLOR_STYLES[color] || GROUP_COLOR_STYLES["bg-blue-400"];
}

export function getAdherentFullName(adherent) {
  return (
    [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ") || "Judoka"
  );
}

export function formatBirthDate(value) {
  if (!value) return "Non renseignée";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function getStatusLabel(status) {
  if (status === "pending") return "En attente";
  if (status === "approved") return "Inscrit";
  if (status === "rejected") return "Refusé";

  return "Statut inconnu";
}

export function getStatusClass(status) {
  if (status === "pending") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (status === "approved") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "rejected") {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/60";
}

export function countAdherentsByStatus(adherentsData = {}) {
  return Object.values(adherentsData)
    .flat()
    .reduce(
      (acc, adherent) => {
        if (adherent.status === "pending") acc.pending += 1;
        else if (adherent.status === "approved") acc.approved += 1;
        else if (adherent.status === "rejected") acc.rejected += 1;
        else acc.unknown += 1;

        return acc;
      },
      {
        pending: 0,
        approved: 0,
        rejected: 0,
        unknown: 0,
      },
    );
}

export function buildGroupsStats(groups = [], adherentsData = {}, limits) {
  const allAdherents = Object.values(adherentsData).flat();
  const statusCounts = countAdherentsByStatus(adherentsData);

  return {
    groupsCount: groups.length,
    maxGroups: limits.maxGroups,
    totalAdherents: allAdherents.length,
    maxAdherents: limits.maxAdherents,
    groupProgress: getProgressPercent(groups.length, limits.maxGroups),
    adherentProgress: getProgressPercent(
      allAdherents.length,
      limits.maxAdherents,
    ),
    canCreateGroup: groups.length < limits.maxGroups,
    ...statusCounts,
  };
}

export const DEFAULT_ADHERENT_FORM = {
  nom: "",
  prenom: "",
  genre: "",
  date_naissance: "",
  poids: "",
  club: "",
  pays: "France",
  departement: "",
  licence_number: "",
  licence_url: "",
  photo_url: "",
  tel_adherent: "",
  mail_adherent: "",
};

export const GENDER_OPTIONS = [
  { value: "", label: "Non renseigné" },
  { value: "Masculin", label: "Masculin" },
  { value: "Féminin", label: "Féminin" },
  { value: "Autre", label: "Autre" },
];

export function isValidEmailAddress(value = "") {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return true;

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanValue);
}

export function isValidPhoneNumber(value = "") {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return true;

  const digits = cleanValue.replace(/\D/g, "");

  return digits.length >= 6 && digits.length <= 15;
}

export function normalizePhoneForHref(value = "") {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) return "";

  const hasInternationalPrefix = cleanValue.startsWith("+");
  const digits = cleanValue.replace(/\D/g, "");

  if (!digits) return "";

  return `${hasInternationalPrefix ? "+" : ""}${digits}`;
}

export function normalizeAdherentPayload(form, userId) {
  const cleanPhone = String(form.tel_adherent || "").trim();

  const cleanEmail = String(form.mail_adherent || "")
    .trim()
    .toLowerCase();

  return {
    nom: String(form.nom || "").trim().toUpperCase(),
    prenom: String(form.prenom || "").trim(),
    genre: form.genre || "",
    date_naissance: form.date_naissance || null,

    poids:
      form.poids === "" ||
      form.poids === null ||
      Number.isNaN(Number(form.poids))
        ? null
        : Number(form.poids),

    club: String(form.club || "").trim(),
    pays: String(form.pays || "").trim() || "France",
    departement: String(form.departement || "").trim(),
    licence_number: String(form.licence_number || "").trim(),
    licence_url: String(form.licence_url || "").trim(),
    photo_url: String(form.photo_url || "").trim(),

    tel_adherent: cleanPhone || null,
    mail_adherent: cleanEmail || null,

    user_id: userId,
    userId,
  };
}

export function validateAdherentForm(form) {
  if (!String(form.nom || "").trim()) {
    return "Le nom de l’adhérent est obligatoire.";
  }

  if (!String(form.prenom || "").trim()) {
    return "Le prénom de l’adhérent est obligatoire.";
  }

  if (form.date_naissance) {
    const birthDate = new Date(form.date_naissance);

    if (Number.isNaN(birthDate.getTime())) {
      return "La date de naissance est invalide.";
    }

    if (birthDate > new Date()) {
      return "La date de naissance ne peut pas être dans le futur.";
    }
  }

  if (form.poids !== "" && form.poids !== null && Number(form.poids) < 0) {
    return "Le poids ne peut pas être négatif.";
  }

  if (!isValidPhoneNumber(form.tel_adherent)) {
    return "Le numéro de téléphone renseigné n’est pas valide.";
  }

  if (!isValidEmailAddress(form.mail_adherent)) {
    return "L’adresse e-mail renseignée n’est pas valide.";
  }

  return "";
}

export function dateToInputDate(value) {
  if (!value) return "";

  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

export function buildAdherentEditForm(adherent, groupId) {
  return {
    nom: adherent?.nom || "",
    prenom: adherent?.prenom || "",
    genre: adherent?.genre || "",
    date_naissance: dateToInputDate(adherent?.date_naissance),
    poids: adherent?.poids ?? "",
    club: adherent?.club || "",
    pays: adherent?.pays || "France",
    departement: adherent?.departement || "",
    licence_number: adherent?.licence_number || "",
    licence_url: adherent?.licence_url || "",
    photo_url: adherent?.photo_url || "",
    tel_adherent: adherent?.tel_adherent || "",
    mail_adherent: adherent?.mail_adherent || "",
    target_group_id: groupId ? String(groupId) : "",
  };
}

export function normalizeAdherentUpdatePayload(form) {
  const cleanPhone = String(form.tel_adherent || "").trim();

  const cleanEmail = String(form.mail_adherent || "")
    .trim()
    .toLowerCase();

  return {
    nom: String(form.nom || "").trim().toUpperCase(),
    prenom: String(form.prenom || "").trim(),
    genre: form.genre || "",
    date_naissance: form.date_naissance || null,

    poids:
      form.poids === "" ||
      form.poids === null ||
      Number.isNaN(Number(form.poids))
        ? null
        : Number(form.poids),

    club: String(form.club || "").trim(),
    pays: String(form.pays || "").trim() || "France",
    departement: String(form.departement || "").trim(),
    licence_number: String(form.licence_number || "").trim(),
    licence_url: String(form.licence_url || "").trim(),
    photo_url: String(form.photo_url || "").trim(),

    tel_adherent: cleanPhone || null,
    mail_adherent: cleanEmail || null,
  };
}